import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, MagnifyingGlass, CaretDown, Gear, SignOut } from "@phosphor-icons/react";
import { useAuth } from "@/contexts/AuthContext";
import { PORTAL_BASE } from "../lib/nav";

export default function Header() {
  const { user, logout } = useAuth();
  const [profileOpen, setProfileOpen] = useState(false);
  const profileRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    const handler = (e) => {
      if (profileRef.current && !profileRef.current.contains(e.target)) {
        setProfileOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const handleLogout = () => {
    setProfileOpen(false);
    logout();
    navigate("/login");
  };

  const initial = (user?.name?.[0] || user?.email?.[0] || "A").toUpperCase();

  return (
    <header
      className="h-14 flex items-center justify-between px-5 border-b border-slate-200 bg-white shrink-0 z-20"
      data-testid="admin-header"
    >
      {/* Search */}
      <div
        className="flex items-center gap-2 bg-slate-50 border border-slate-200 px-3 py-2 w-60 xl:w-80 rounded-lg opacity-40 cursor-not-allowed"
        title="Search — coming soon"
      >
        <MagnifyingGlass size={13} className="text-slate-400 shrink-0" />
        <span className="text-sm text-slate-400 select-none">Search coming soon…</span>
      </div>

      {/* Right side */}
      <div className="flex items-center gap-1">
        {/* Notifications */}
        <button
          className="relative w-9 h-9 flex items-center justify-center text-slate-500 hover:text-slate-700 hover:bg-slate-100 rounded-lg transition-colors"
          aria-label="Notifications"
          data-testid="admin-notifications"
        >
          <Bell size={16} />
        </button>

        <div className="w-px h-4 bg-slate-200 mx-2" />

        {/* Profile */}
        <div className="relative" ref={profileRef}>
          <button
            onClick={() => setProfileOpen((v) => !v)}
            className="flex items-center gap-2.5 px-2.5 py-1.5 hover:bg-slate-50 rounded-lg transition-colors"
            data-testid="admin-profile-toggle"
            aria-expanded={profileOpen}
            aria-haspopup="true"
          >
            {/* Avatar */}
            <div className="w-7 h-7 bg-blue-600 rounded-full flex items-center justify-center shrink-0">
              <span className="text-[11px] font-bold text-white">{initial}</span>
            </div>

            {/* Name + role */}
            <div className="hidden xl:block text-left">
              <div className="text-sm font-medium text-slate-700 truncate max-w-[110px] leading-none">
                {user?.name || "Admin"}
              </div>
              <div className="text-xs text-slate-400 mt-0.5">
                {user?.role || "admin"}
              </div>
            </div>

            <CaretDown
              size={11}
              className={`text-slate-400 transition-transform duration-150 ${profileOpen ? "rotate-180" : ""}`}
            />
          </button>

          {/* Dropdown */}
          {profileOpen && (
            <div
              className="absolute right-0 top-full mt-1.5 w-52 bg-white border border-slate-200 rounded-xl shadow-xl z-50 overflow-hidden"
              data-testid="admin-profile-dropdown"
            >
              {/* User info */}
              <div className="px-4 py-3 border-b border-slate-100">
                <div className="text-sm font-medium text-slate-700 truncate">
                  {user?.name || "Admin"}
                </div>
                <div className="text-xs text-slate-400 truncate mt-0.5">{user?.email}</div>
                <div className="mt-2">
                  <span className="inline-flex items-center bg-blue-50 text-blue-600 border border-blue-200 text-xs font-semibold rounded-full px-2.5 py-0.5">
                    {user?.role || "admin"}
                  </span>
                </div>
              </div>

              {/* Actions */}
              <div className="py-1">
                <button
                  onClick={() => {
                    setProfileOpen(false);
                    navigate(`${PORTAL_BASE}/system/settings`);
                  }}
                  className="flex items-center gap-3 w-full px-4 py-2.5 text-xs font-medium text-slate-700 hover:bg-slate-50 transition-colors"
                  data-testid="admin-settings-link"
                >
                  <Gear size={14} />
                  Settings
                </button>
                <button
                  onClick={handleLogout}
                  className="flex items-center gap-3 w-full px-4 py-2.5 text-xs font-medium text-slate-700 hover:bg-slate-50 transition-colors border-t border-slate-100"
                  data-testid="admin-logout"
                >
                  <SignOut size={14} />
                  Sign out
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}
